import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { MenuItem } from '../../shared/navigation/menu'
import { getNavigationPreferences, recordNavigationVisit, setNavigationFavorite } from './navigationPreferencesApi'

export type NavigationPreferencesValue = {
  favorites: MenuItem[]
  recent: MenuItem[]
  isLoading: boolean
  error: string | null
  isFavorite: (key: string) => boolean
  toggleFavorite: (item: MenuItem) => Promise<void>
}

const NavigationPreferencesContext = createContext<NavigationPreferencesValue | null>(null)

export function useNavigationPreferencesState(items: MenuItem[], currentItem: MenuItem | undefined,
  identityKey: string): NavigationPreferencesValue {
  const itemMap = useMemo(() => new Map(items
    .filter(item => !item.children?.length && item.key !== 'home')
    .map(item => [item.key, item])), [items])
  const [favoriteKeys, setFavoriteKeys] = useState<string[]>([])
  const [recentKeys, setRecentKeys] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const recordedKey = useRef<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true); setError(null); recordedKey.current = null
    void getNavigationPreferences(controller.signal)
      .then(value => { setFavoriteKeys(value.favoriteMenuKeys); setRecentKeys(value.recentMenuKeys) })
      .catch(reason => {
        if (!(reason instanceof DOMException && reason.name === 'AbortError')) setError('home.loadError')
      })
      .finally(() => { if (!controller.signal.aborted) setIsLoading(false) })
    return () => controller.abort()
  }, [identityKey])

  useEffect(() => {
    if (isLoading || !currentItem || !itemMap.has(currentItem.key) || recordedKey.current === currentItem.key) return
    recordedKey.current = currentItem.key
    void recordNavigationVisit(currentItem.key)
      .then(value => setRecentKeys(value.recentMenuKeys))
      .catch(() => { recordedKey.current = null })
  }, [currentItem, isLoading, itemMap])

  const toggleFavorite = useCallback(async (item: MenuItem) => {
    if (!itemMap.has(item.key)) return
    const next = !favoriteKeys.includes(item.key)
    setError(null)
    setFavoriteKeys(current => next ? [...current, item.key] : current.filter(key => key !== item.key))
    try {
      const value = await setNavigationFavorite(item.key, next)
      setFavoriteKeys(value.favoriteMenuKeys)
    } catch {
      setFavoriteKeys(current => next ? current.filter(key => key !== item.key) : [...current, item.key])
      setError('home.saveError')
    }
  }, [favoriteKeys, itemMap])

  return {
    favorites: favoriteKeys.flatMap(key => itemMap.get(key) ?? []),
    recent: recentKeys.flatMap(key => itemMap.get(key) ?? []).slice(0, 10),
    isLoading, error,
    isFavorite: key => favoriteKeys.includes(key),
    toggleFavorite,
  }
}

export function NavigationPreferencesProvider({ value, children }:
  PropsWithChildren<{ value: NavigationPreferencesValue }>) {
  return <NavigationPreferencesContext.Provider value={value}>{children}</NavigationPreferencesContext.Provider>
}

export function useNavigationPreferences() {
  const value = useContext(NavigationPreferencesContext)
  if (!value) throw new Error('useNavigationPreferences must be used inside NavigationPreferencesProvider')
  return value
}
